import { SAMPLE_INTERVAL } from '../../config/chartDisplay'

export interface VarianceBin {
  distance: number
  lat: number
  lng: number
}

const hasGps = (bin: VarianceBin): boolean => bin.lat !== 0 || bin.lng !== 0

function nextBinWithGps(bins: VarianceBin[], after: number): number {
  for (let j = after + 1; j < bins.length; j++) {
    if (hasGps(bins[j])) return j
  }
  return -1
}

function fillFromNeighbours(
  bins: VarianceBin[],
  i: number,
  previous: number,
  next: number
): void {
  if (previous >= 0 && next >= 0) {
    const t = (i - previous) / (next - previous)
    bins[i].lat = bins[previous].lat + t * (bins[next].lat - bins[previous].lat)
    bins[i].lng = bins[previous].lng + t * (bins[next].lng - bins[previous].lng)
  } else if (previous >= 0) {
    bins[i].lat = bins[previous].lat
    bins[i].lng = bins[previous].lng
  } else if (next >= 0) {
    bins[i].lat = bins[next].lat
    bins[i].lng = bins[next].lng
  }
}

function interpolateGpsGaps(bins: VarianceBin[]): void {
  let previous = -1
  let next = nextBinWithGps(bins, -1)
  for (let i = 0; i < bins.length; i++) {
    if (next === i) next = nextBinWithGps(bins, i)
    if (!hasGps(bins[i])) fillFromNeighbours(bins, i, previous, next)
    if (hasGps(bins[i])) previous = i
  }
}

/**
 * Fills bin GPS from SpeedTrace points, whose `distance` sits on the same
 * recalibrated frame the bins are indexed in (pass 29), so the gradient's
 * coordinates line up with the recalibrated bin positions instead of raw
 * odometer ones.
 */
export function ensureBinGpsFromTraces(
  bins: VarianceBin[],
  traces: { points: { distance: number; lat?: number; lng?: number }[] }[],
  startDist: number
): void {
  const numBins = bins.length
  if (!bins.some(hasGps)) {
    for (const trace of traces) {
      for (const p of trace.points) {
        if (p.lat == null || p.lng == null || (p.lat === 0 && p.lng === 0))
          continue
        const binIdx = Math.floor((p.distance - startDist) / SAMPLE_INTERVAL)
        if (binIdx < 0 || binIdx >= numBins) continue
        if (!hasGps(bins[binIdx])) {
          bins[binIdx].lat = p.lat
          bins[binIdx].lng = p.lng
        }
      }
      if (bins.some(hasGps)) break
    }
  }

  if (!bins.some(hasGps)) return

  interpolateGpsGaps(bins)
}
